import React, { forwardRef, useLayoutEffect, useRef, useCallback } from 'react';
import styles from './ChatInputAreaUI.module.css';

export interface ChatInputAreaUIProps {
  value: string;
  placeholder?: string;
  disabled?: boolean;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

function mergeRefs<T>(
  ...refs: (React.Ref<T> | undefined)[]
): (instance: T | null) => void {
  return (instance) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(instance);
      } else if (ref && typeof ref === 'object' && 'current' in ref) {
        (ref as React.MutableRefObject<T | null>).current = instance;
      }
    });
  };
}

const MAX_INPUT_HEIGHT_PX = 200;

export const ChatInputAreaUI = forwardRef<HTMLTextAreaElement, ChatInputAreaUIProps>(
  function ChatInputAreaUIInternal(
    {
      value,
      placeholder = 'Reply, @ for context',
      disabled = false,
      onChange,
      onKeyDown,
      onPaste,
      onFocus,
      onBlur,
    },
    forwardedRef
  ) {
    const internalRef = useRef<HTMLTextAreaElement>(null);

    const adjustHeight = useCallback(() => {
      const textarea = internalRef.current;
      if (!textarea) return;
      textarea.style.height = 'auto';
      const next = Math.min(textarea.scrollHeight, MAX_INPUT_HEIGHT_PX);
      textarea.style.height = `${next}px`;
    }, []);

    useLayoutEffect(() => {
      adjustHeight();
    }, [adjustHeight, value]);

    return (
      <div className={styles.chatInputArea}>
        <textarea
          ref={mergeRefs(internalRef, forwardedRef)}
          className={styles.chatInputAreaTextarea}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onFocus={onFocus}
          onBlur={onBlur}
          rows={1}
        />
      </div>
    );
  }
);

ChatInputAreaUI.displayName = 'ChatInputAreaUI';
